import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';
import { queue } from '@nullvoxpopuli/ember-composable-helpers';
import type { Task } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import EmberTooltip from 'ember-tooltips/components/ember-tooltip';
import UiButton from 'garaje/components/ui-button';
import type { ReportSource } from 'garaje/models/approval-status';
import type ConnectInvite from 'garaje/models/connect-invite';
import type ScreenerResult from 'garaje/models/screener-result';
import type { GuestWithVisitApproval } from 'garaje/models/visit-approval';
import type VisitApproval from 'garaje/models/visit-approval';
import { localCopy } from 'tracked-toolbox';

interface ReviewButtonsSignature {
  Args: {
    approveTask: Task<void, [ReportSource[]]>;
    denyTask?: Task<void, []>;
    disabled?: boolean;
    result: ScreenerResult;
    model: GuestWithVisitApproval | ConnectInvite;
    onApprove?: () => void;
    onDeny?: () => void;
  };
  Blocks: {
    deny: [];
  };
}

export default class ReviewButtons extends Component<ReviewButtonsSignature> {
  @localCopy('args.onApprove', () => {}) onApprove!: () => void;
  @localCopy('args.onDeny', () => {}) onDeny!: () => void;

  get visitApproval(): VisitApproval | undefined {
    if ('visitApproval' in this.args.model) {
      return this.args.model.visitApproval;
    }
    return undefined;
  }

  get disabled(): boolean {
    return Boolean(
      this.args.approveTask.isRunning ||
        this.args.denyTask?.isRunning ||
        this.visitApproval?.idScanningPendingResult ||
        this.args.disabled,
    );
  }

  <template>
    <div class="flex {{if @denyTask 'flex-row gap-x-2' 'flex-col'}}">
      <UiButton
        class="btn btn--small btn--outline !bg-white w-full mt-2"
        data-test-approve-button={{@result.screenerSource}}
        disabled={{this.disabled}}
        {{on "click" (queue (perform @approveTask (array @result.screenerSource)) this.onApprove)}}
      >
        {{svgJar "checkmark-green" height="12" width="12" class="flex-none mr-2 align-middle"}}
        Approve
      </UiButton>
      {{#if @denyTask}}
        <UiButton
          class="btn btn--small btn--outline !bg-white w-full mt-2"
          data-test-deny-button={{@result.screenerSource}}
          disabled={{this.disabled}}
          {{on "click" (queue (perform @denyTask) this.onDeny)}}
        >
          {{svgJar "cross-small-red" height="12" width="12" class="flex-none mr-2 align-middle"}}
          {{#if (has-block "deny")}}
            {{yield to="deny"}}
          {{else}}
            Deny
          {{/if}}
        </UiButton>
      {{/if}}
      {{#if this.visitApproval.idScanningPendingResult}}
        <EmberTooltip @side="top" @text="Please wait until the visitor's ID is done processing." />
      {{/if}}
    </div>
  </template>
}
